import { useState, useEffect, useContext, useRef } from 'react';
import { STRIPE_PUBLISH_KEY } from '../../constants';
import { Link, useNavigate, useParams } from 'react-router-dom';
import withAuth from '../../auth';
import { Breadcrumb } from 'react-bootstrap';
import moment from 'moment';
import { AuthContext } from '../AuthContext';
import CardForm from '../CardForm';

import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Spinner from 'react-bootstrap/Spinner';
import InputMask from 'react-input-mask';
import { handleApiRequest } from '../../utils/apiHandler';
import api from '../../services/axios';
import { renderStars } from '../../utils/renderStars';
import useNotification from "../../hooks/useNotification";

const stripePromise = loadStripe(`${STRIPE_PUBLISH_KEY}`);
const UserProfile = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const { showError, showSuccess } = useNotification();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone_num: '',
    address: '',
    progress: '',
    note_for_driver: '',

    business_name: '',
    designation: '',
    company_email: '',
    company_address: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [isNotificationSent, setIsNotificationSent] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  const [smsMessage, setSmsMessage] = useState('');
  const [isSmsSent, setIsSmsSent] = useState(false);
  const [showSms, setShowSms] = useState(false);

  const noteForDriverRef = useRef(null);

  const [billingSettings, setBillingSettings] = useState(null);
  const [isBillingSettingsSubmitted, setIsBillingSettingsSubmitted] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    getBillingSettings();
  }, [userId]);

  useEffect(() => {
    if (billingSettings?.card_payment_allowed) {
      getPaymentMethods();
    }
  }, [billingSettings]);

  const fetchUserDetails = async () => {
    await handleApiRequest({
      requestFn: () => api.get(`/manager/users/${userId}`),
      onSuccess: (res) => setUser(res.data),
      onError: showError,
    });
  };

  const getPaymentMethods = async () => {
    setPaymentMethods(null);

    await handleApiRequest({
      requestFn: () => api.get(`/manager/users/${userId}/payment-methods`),
      onSuccess: (res) => {
        setPaymentMethods(res.data);
      },
      onError: showError,
      finallyFn: () => {},
    });
  };

  const getBillingSettings = async () => {
    await handleApiRequest({
      requestFn: () => api.get(`/manager/users/${userId}/billing-settings`),
      onSuccess: (res) => setBillingSettings(res.settings),
      onError: showError,
    });
  };

  const updateBillingSettings = async () => {
    if (!window.confirm("Update this customer's billing settings?")) return;

    setIsBillingSettingsSubmitted(true);

    await handleApiRequest({
      requestFn: () =>
          api.put(`/manager/users/${userId}/billing-settings`, {
            invoice_issue_mode: billingSettings.invoice_issue_mode,
            invoice_due_mode: billingSettings.invoice_due_mode || 'on_receipt',
            invoice_due_days: billingSettings.show_due_date
                ? billingSettings.invoice_due_days
                : null,
            show_due_date: !!billingSettings.show_due_date,
            allowed_payment_methods: billingSettings.allowed_payment_methods || ['invoice'],
            is_auto_invoice_enabled: !!billingSettings.is_auto_invoice_enabled,
            next_invoice_date: billingSettings.next_invoice_date || null,
          }),
      onSuccess: (res) => {
        showSuccess(res.message);
        getBillingSettings();
      },
      onError: showError,
      finallyFn: () => setIsBillingSettingsSubmitted(false),
    });
  };

  const toggleAllowedPaymentMethod = (method) => {
    const current = billingSettings.allowed_payment_methods || [];
    let next = current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method];

    if (next.length === 0) {
      next = ['invoice'];
    }

    setBillingSettings({
      ...billingSettings,
      allowed_payment_methods: next,
    });
  };

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        password: '',
        phone_num: user.phone_num || '',
        address: user.address || '',
        progress: user.progress || '',
        note_for_driver: user.note_for_driver || '',

        business_name: user.company?.business_name || '',
        designation: user.company?.designation || '',
        company_email: user.company?.email || '',
        company_address: user.company?.address || '',
      });
    }
  }, [user]);

  const handleDeleteClick = async () => {
    if (!window.confirm('Are you sure that you want to delete this customer?')) return;

    await handleApiRequest({
      requestFn: () => api.delete(`/manager/users/${userId}`),
      onSuccess: (res) => {
        showSuccess('User deleted successfully!');
        setUser(null);
        navigate('/users');
      },
      onError: showError,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!window.confirm("Are you sure that you want to update the customer's data?")) return;

    setIsSubmitted(true);

    await handleApiRequest({
      requestFn: () => api.put(`/manager/users/${userId}`, formData),
      onSuccess: (res) => {
        showSuccess(res.message);
        setUser(res.user);
      },
      onError: showError,
      finallyFn: () => {
        setIsSubmitted(false);
      },
    });
  };

  const handleSendPushNotification = async () => {
    if (!window.confirm('Are you sure that you want to send the push notification to this user?')) return false;

    setIsNotificationSent(true);

    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/users/${userId}/notify`, {
          title: notificationTitle,
          body: notificationBody,
        }),
      onSuccess: (res) => {
        showSuccess('Push notification successfully sent!');
        setNotificationTitle('');
        setNotificationBody('');
      },
      onError: showError,
      finallyFn: () => {
        setIsNotificationSent(false);
      },
    });
  };

  const handleSendSms = async () => {
    if (!window.confirm('Are you sure that you want to send the SMS to this customer?')) return false;

    setIsSmsSent(true);
    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/users/${userId}/send-sms`, {
          message: smsMessage,
        }),
      onSuccess: (res) => {
        showSuccess('SMS successfully sent!');
        setSmsMessage('');
      },
      onError: showError,
      finallyFn: () => {
        setIsSmsSent(false);
      },
    });
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [formData.note_for_driver]);
  const adjustTextareaHeight = () => {
    if (noteForDriverRef.current) {
      noteForDriverRef.current.style.height = 'auto';
      noteForDriverRef.current.style.overflow = 'hidden';
      noteForDriverRef.current.style.height = `${noteForDriverRef.current.scrollHeight}px`;
    }
  };

  return (
    <div className="container-fluid mt-3">
      <Breadcrumb>
        <Breadcrumb.Item href="/users">Customers</Breadcrumb.Item>
        <Breadcrumb.Item active>Customer Profile</Breadcrumb.Item>
      </Breadcrumb>
      <h2 className="text-center" style={{ position: 'relative' }}>
        Customer Profile {user ? `#${user.id}` : ''}
        {user && hasPermission('user-update') && (
          <div className="create-pickup-wrap">
            <Link
              to={`/pickups/create/${user.id}`}
              className="create-pickup-btn btn btn-outline-primary"
            >
              Create&nbsp;Pickup
            </Link>
          </div>
        )}
      </h2>
      {user ? (
        <div className="row">
          <div className="col-xl-6">
            <div className="card mb-3">
              <div className="card-body" align={'center'}>
                {user.profile_photo && (
                  <a
                    href={user.profile_photo}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={user.profile_photo}
                      width="100"
                      alt={`${user.name}'s Profile Photo`}
                      className="mb-3"
                    />
                  </a>
                )}
                {user.company && (
                  <div className="mb-2">
                    <h5 className="card-title">Company</h5>
                    <p className="card-text">
                      Business Name: <b>{user.company.business_name}</b>
                    </p>
                  </div>
                )}
                <p className="card-text">
                  <b>{user.full_name}</b>
                  <br />
                </p>
                {hasPermission('user-update') ? (
                  <>
                    {user.company && (
                      <>
                        <div className="form-group row mb-2">
                          <label className="col-md-3 col-form-label">
                            Business Name:
                          </label>
                          <div className="col-md-9">
                            <input
                              type="text"
                              className="form-control"
                              name="business_name"
                              value={formData.business_name}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <div className="form-group row mb-2">
                          <label className="col-md-3 col-form-label">
                            Designation:
                          </label>
                          <div className="col-md-9">
                            <input
                              type="text"
                              className="form-control"
                              name="designation"
                              value={formData.designation}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <div className="form-group row mb-2">
                          <label className="col-md-3 col-form-label">
                            Company Email:
                          </label>
                          <div className="col-md-9">
                            <input
                              type="email"
                              className="form-control"
                              name="company_email"
                              value={formData.company_email}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <div className="form-group row mb-2">
                          <label className="col-md-3 col-form-label">
                            Company Address:
                          </label>
                          <div className="col-md-9">
                            <input
                                type="text"
                                className="form-control"
                                name="company_address"
                                value={formData.company_address}
                                onChange={handleChange}
                                placeholder="Company address"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    <hr />
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">Email:</label>
                      <div className="col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">Phone:</label>
                      <div className="col-md-9">
                        <InputMask
                          className="form-control"
                          mask="+9 (999) 999-9999"
                          name="phone_num"
                          value={formData.phone_num}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">Address:</label>
                      <div className="col-md-9">
                        <input
                            type="text"
                            className="form-control"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Customer address"
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">
                        First Name:
                      </label>
                      <div className="col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">
                        Last Name:
                      </label>
                      <div className="col-md-9">
                        <input
                          type="text"
                          className="form-control"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    {/*<div className="form-group row mb-2">*/}
                    {/*  <label className="col-md-3 col-form-label">*/}
                    {/*    Progress:*/}
                    {/*  </label>*/}
                    {/*  <div className="col-md-9">*/}
                    {/*    <select*/}
                    {/*      className="custom-select mr-sm-2"*/}
                    {/*      name="progress"*/}
                    {/*      value={formData.progress}*/}
                    {/*      onChange={handleChange}*/}
                    {/*    >*/}
                    {/*      <option value="1">1</option>*/}
                    {/*      <option value="2">2</option>*/}
                    {/*      <option value="3">3</option>*/}
                    {/*    </select>*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">
                        Note for Driver:
                      </label>
                      <div className="col-md-9">
                        <textarea
                          ref={noteForDriverRef}
                          value={formData.note_for_driver}
                          className="form-control"
                          name="note_for_driver"
                          onChange={(e) => {
                            handleChange(e);
                            adjustTextareaHeight();
                          }}
                          placeholder="Note for Driver"
                          rows={1}
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-2">
                      <label className="col-md-3 col-form-label">
                        New Password:
                      </label>
                      <div className="col-md-9">
                        <input
                          type="password"
                          className="form-control"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    <div className="d-flex justify-content-center">
                      <button className="btn btn-primary" onClick={handleSave}>
                        {isSubmitted ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            &nbsp;Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                    <hr />
                    <button
                      className="btn btn-danger"
                      onClick={handleDeleteClick}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <p className="card-text">
                      Email: <b>{user.email}</b>
                      <br />
                      Phone: <b>{user.phone_num}</b>
                      <br />
                      Address: <b>{user.address || 'not set'}</b>
                      <br />
                      First Name: <b>{user.first_name}</b>
                      <br />
                      Last Name: <b>{user.last_name}</b>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="text-center">Details</h5>
                <div className="card-text">
                  Average Rating: <b>{renderStars(user.avg_rating || 0)}</b>
                  <p className="card-text">
                    Registered:{' '}
                    <b>{moment(user.created_at).format('YYYY-MM-DD HH:mm')}</b>
                    <br />
                    Last Login:{' '}
                    <b>
                      {user.last_login_at
                        ? moment(user.last_login_at).format('YYYY-MM-DD HH:mm')
                        : 'unknown '}
                    </b>
                    <br />
                    IP: <b>{user.last_login_ip || 'unknown'}</b>
                  </p>
                </div>
              </div>
            </div>
            {hasPermission('user-update') && (
              <div className="card mb-3">
                <div className="card-body">
                  <h5
                    className="text-center"
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => setShowNotification(!showNotification)}
                  >
                    Send Push Notification {showNotification ? '▼' : '▶'}
                  </h5>
                  <div
                    className={`fade-section ${
                      showNotification ? 'show' : 'hide'
                    }`}
                  >
                    <div className="form-group mb-2">
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Title"
                        value={notificationTitle}
                        onChange={(e) => setNotificationTitle(e.target.value)}
                      />
                    </div>
                    <div className="form-group mb-2">
                      <textarea
                        className="form-control"
                        style={{ minHeight: '50px' }}
                        placeholder="Body"
                        value={notificationBody}
                        onChange={(e) => setNotificationBody(e.target.value)}
                      />
                    </div>
                    <div align={'center'}>
                      <button
                        className="btn btn-primary"
                        onClick={handleSendPushNotification}
                      >
                        {isNotificationSent ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            &nbsp;Sending...
                          </>
                        ) : (
                          'Send'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {hasPermission('user-update') && (
              <div className="card mb-3">
                <div className="card-body">
                  <h5
                    className="text-center"
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={() => setShowSms(!showSms)}
                  >
                    Send SMS {showSms ? '▼' : '▶'}
                  </h5>
                  <div className={`fade-section ${showSms ? 'show' : 'hide'}`}>
                    <div className="form-group mb-2">
                      <textarea
                        className="form-control"
                        style={{ minHeight: '50px' }}
                        placeholder="Message"
                        value={smsMessage}
                        onChange={(e) => setSmsMessage(e.target.value)}
                      />
                    </div>
                    <div align={'center'}>
                      <button
                        className="btn btn-primary"
                        onClick={handleSendSms}
                      >
                        {isSmsSent ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            &nbsp;Sending...
                          </>
                        ) : (
                          'Send'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {hasPermission('user-update') && billingSettings && (
            <div className="col-xl-6">
              <div className="card mb-3">
                <div className="card-body">
                    <h5 className="text-center">Billing Settings</h5>

                    <div className="form-group mb-2">
                      <label>Invoice Issue Mode</label>
                      <select
                          className="form-control"
                          value={billingSettings.invoice_issue_mode}
                          onChange={(e) =>
                              setBillingSettings({
                                ...billingSettings,
                                invoice_issue_mode: e.target.value,
                              })
                          }
                      >
                        <option value="per_pickup">Pay upon receipt after each pickup</option>
                        <option value="semi_monthly">Semi-monthly: 15th and month end</option>
                        <option value="monthly">Monthly: month end</option>
                      </select>
                    </div>

                    <div className="form-check mb-2">
                      <label className="form-check-label">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!!billingSettings.show_due_date}
                            onChange={(e) =>
                                setBillingSettings({
                                  ...billingSettings,
                                  show_due_date: e.target.checked,
                                })
                            }
                        />
                        Show due date on invoice
                      </label>
                    </div>

                    {billingSettings.show_due_date && (
                        <div className="form-group mb-2">
                          <label>Due Days</label>
                          <input
                              type="number"
                              min="1"
                              max="365"
                              className="form-control"
                              value={billingSettings.invoice_due_days || ''}
                              onChange={(e) =>
                                  setBillingSettings({
                                    ...billingSettings,
                                    invoice_due_days: e.target.value,
                                  })
                              }
                          />
                        </div>
                    )}

                    <div className="mb-2">
                      <label>Allowed Payment Methods</label>

                      <div className="form-check">
                        <label className="form-check-label">
                          <input
                              className="form-check-input"
                              type="checkbox"
                              checked={(billingSettings.allowed_payment_methods || []).includes('invoice')}
                              onChange={() => toggleAllowedPaymentMethod('invoice')}
                          />
                          Invoice / Terms
                        </label>
                      </div>

                      <div className="form-check">
                        <label className="form-check-label">
                          <input
                              className="form-check-input"
                              type="checkbox"
                              checked={(billingSettings.allowed_payment_methods || []).includes('card')}
                              onChange={() => toggleAllowedPaymentMethod('card')}
                          />
                          Credit Card
                        </label>
                      </div>
                    </div>

                    <div className="form-check mb-3">
                      <label className="form-check-label">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!!billingSettings.is_auto_invoice_enabled}
                            onChange={(e) =>
                                setBillingSettings({
                                  ...billingSettings,
                                  is_auto_invoice_enabled: e.target.checked,
                                })
                            }
                        />
                        Auto invoice enabled
                      </label>
                    </div>

                    <div className="text-center">
                      <button className="btn btn-primary" onClick={updateBillingSettings}>
                        {isBillingSettingsSubmitted ? 'Saving...' : 'Save Billing Settings'}
                      </button>
                    </div>
                  </div>
              </div>
            </div>
          )}
          {hasPermission('user-update') && billingSettings?.card_payment_allowed && (
            <div className="col-xl-6">
              <div className="card mb-3">
                <div className="card-body">
                  <h5 className="text-center">Payment methods</h5>
                  {paymentMethods ? (
                    paymentMethods.length > 0 ? (
                      <Elements stripe={stripePromise}>
                        <ul className="list-group mb-2">
                          {paymentMethods.map((method) => (
                            <li
                              key={method.id}
                              className="list-group-item d-flex justify-content-between"
                            >
                              <b>
                                <span style={{ textTransform: 'uppercase' }}>
                                  {method.card?.brand}
                                </span>{' '}
                                **** **** **** {method.card?.last4}
                              </b>
                              <div>
                                Expires{' '}
                                {method.card?.exp_month
                                  .toString()
                                  .padStart(2, '0')}{' '}
                                / {method.card?.exp_year}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </Elements>
                    ) : (
                      <div className="text-center">
                        No payment methods available.
                      </div>
                    )
                  ) : (
                    <div
                      className="d-flex justify-content-center"
                      style={{ padding: '20px 0' }}
                    >
                      <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  )}
                  <hr />
                  <h5 className="text-center">Add a new card</h5>
                  <Elements stripe={stripePromise}>
                    <CardForm
                      onCardAdded={getPaymentMethods}
                      userId={userId}
                      onError={showError}
                    />
                  </Elements>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '80vh' }}
        >
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .create-pickup-wrap {
          position: absolute;
          top: 50%;
          left: 100%;
          transform: translate(-100%, -55%);
        }
        @media only screen and (max-width: 768px) {
          .create-pickup-wrap {
            position: static;
            transform: translateX(0);
          }
          .create-pickup-btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
            line-height: 1.5;
            border-radius: 0.2rem;
          }
        }

        .fade-section {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
        }

        .fade-section.show {
          max-height: 500px;
          opacity: 1;
        }

        .fade-section.hide {
          max-height: 0;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default withAuth(UserProfile);
